import React from 'react';
import { 
  Typography, 
  Grid, 
  Box,
  Card,
  CardContent,
  Stack,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Paper
} from '@mui/material';
import PageTemplate from '../components/PageTemplate';
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  ArrowForward as ArrowForwardIcon,
  Timer as TimerIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const HowItWorksPage: React.FC = () => {
  return (
    <PageTemplate 
      title="How It Works" 
      description="Learn how to use 1TimeMail to protect your privacy online with our simple step-by-step guide."
      breadcrumbs={[{ label: 'How It Works', path: '/how-it-works' }]}
    >
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Simple, Fast, and Secure
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 800 }}>
          Using 1TimeMail is incredibly straightforward. Our service is designed to be intuitive and hassle-free, allowing you to create and use temporary email addresses in seconds.
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ mb: 6, p: 0, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Box sx={{ p: 4, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" gutterBottom>
            Step-by-Step Guide
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Follow these simple steps to use 1TimeMail for your temporary email needs.
          </Typography>
        </Box>
        
        <Stepper orientation="vertical" sx={{ p: 4 }}>
          <Step active={true}>
            <StepLabel>
              <Typography variant="h6">Generate Your Email Address</Typography>
            </StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" paragraph>
                Visit the 1TimeMail homepage and your temporary email address will be automatically generated.
                No sign-up or personal information required.
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  href="/"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Try It Now
                </Button>
              </Box>
            </StepContent>
          </Step>
          
          <Step active={true}>
            <StepLabel>
              <Typography variant="h6">Copy Your Email Address</Typography>
            </StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" paragraph>
                Use the copy button to easily copy your temporary email address to the clipboard.
                Then use it when registering on websites, forums, or any online service.
              </Typography>
            </StepContent>
          </Step>
          
          <Step active={true}>
            <StepLabel>
              <Typography variant="h6">Receive Emails Instantly</Typography>
            </StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" paragraph>
                Any emails sent to your temporary address will appear instantly in your inbox.
                No refreshing needed — we use real-time updates to show new messages as they arrive.
              </Typography>
            </StepContent>
          </Step>
          
          <Step active={true}>
            <StepLabel>
              <Typography variant="h6">Read and Reply</Typography>
            </StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" paragraph>
                Open any email to read its contents. You can view HTML emails, attachments, and even reply
                to messages directly from your temporary inbox.
              </Typography>
            </StepContent>
          </Step>
          
          <Step active={true}>
            <StepLabel>
              <Typography variant="h6">Auto-Deletion</Typography>
            </StepLabel>
            <StepContent>
              <Typography variant="body2" color="text.secondary" paragraph>
                Your temporary email and all its contents are automatically deleted after 24 hours for
                maximum privacy. No action required on your part.
              </Typography>
            </StepContent>
          </Step>
        </Stepper>
      </Paper>

      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          When to Use 1TimeMail
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card 
              elevation={0}
              sx={{ 
                height: '100%',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Perfect For
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon color="success" />
                    <Typography variant="body2">
                      <strong>Online Shopping:</strong> Sign up for promotional offers without filling your inbox with marketing emails
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon color="success" />
                    <Typography variant="body2">
                      <strong>Trials and Subscriptions:</strong> Register for free trials without commitment
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon color="success" />
                    <Typography variant="body2">
                      <strong>Forums and Communities:</strong> Participate in online discussions without revealing your personal email
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon color="success" />
                    <Typography variant="body2">
                      <strong>App Downloads:</strong> Download apps that require email verification
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon color="success" />
                    <Typography variant="body2">
                      <strong>Classified Sites:</strong> Stay anonymous when posting or responding to classified ads
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card 
              elevation={0}
              sx={{ 
                height: '100%',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Not Recommended For
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <DeleteIcon color="error" />
                    <Typography variant="body2">
                      <strong>Important Accounts:</strong> Banking, financial services, or government accounts
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <DeleteIcon color="error" />
                    <Typography variant="body2">
                      <strong>Long-term Communication:</strong> Ongoing business or personal correspondence
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <DeleteIcon color="error" />
                    <Typography variant="body2">
                      <strong>Social Media:</strong> Primary accounts on platforms you use regularly
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <DeleteIcon color="error" />
                    <Typography variant="body2">
                      <strong>Job Applications:</strong> Applying for jobs or professional networking
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <DeleteIcon color="error" />
                    <Typography variant="body2">
                      <strong>Recovery Emails:</strong> Password reset or account recovery for important services
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      <Divider sx={{ my: 6 }} />
      
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Frequently Asked Questions
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              How long does a temporary email last?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Your temporary email address and all received messages are automatically deleted after 24 hours for free users. Premium users can extend this period up to 7 days.
            </Typography>
            
            <Typography variant="h6" gutterBottom>
              Can I receive attachments?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Yes, you can receive and download attachments of up to 10MB in size with a free account. Premium accounts can receive larger attachments up to 25MB.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Is 1TimeMail completely free?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Yes, the basic service is completely free with no hidden costs. We also offer a Premium option with additional features for users who need enhanced functionality.
            </Typography>
            
            <Typography variant="h6" gutterBottom>
              Can I reply to emails I receive?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Yes, you can send replies from your temporary email address. Free accounts can send up to 3 replies per email address, while Premium accounts have unlimited replies.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </PageTemplate>
  );
};

export default HowItWorksPage; 